import { useState, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2 } from "lucide-react";
import { useHospitalityApplication, HospitalityApplicationData } from "@/hooks/use-hospitality-application";

const SCHOOLS = [
  { value: "ehl", label: "École hôtelière de Lausanne (EHL)" },
  { value: "ehsr", label: "EHSR Sierre" },
  { value: "iffp", label: "IFFP" },
  { value: "belvoirpark", label: "Belvoirpark" },
  { value: "cesar_ritz", label: "César Ritz Colleges" },
  { value: "other", label: "Autre école suisse" },
];

const HospitalityApplicationForm = () => {
  const { mutate, isPending, isSuccess } = useHospitalityApplication();
  const [form, setForm] = useState<HospitalityApplicationData>({
    fullName: "",
    email: "",
    phone: "",
    school: "",
    graduationYear: "",
    restaurantName: "",
    restaurantOpeningDate: "",
    city: "",
    message: "",
    honeypot: "",
  });

  const update = (key: keyof HospitalityApplicationData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate(form);
  };

  if (isSuccess) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-10 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-playfair text-2xl font-semibold text-foreground">
            Candidature envoyée
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Tu vas recevoir un email de confirmation. Nous reviendrons vers toi sous 3 jours ouvrés avec les prochaines étapes (vérification du diplôme et du registre du commerce).
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardContent className="p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-5">
          <input
            type="text"
            name="honeypot"
            value={form.honeypot}
            onChange={update("honeypot")}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
          />

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Nom complet *</Label>
              <Input
                id="fullName"
                required
                value={form.fullName}
                onChange={update("fullName")}
                placeholder="Marie Dupont"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="marie@tonresto.ch"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+41 79 123 45 67"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Ville *</Label>
              <Input
                id="city"
                required
                value={form.city}
                onChange={update("city")}
                placeholder="Lausanne"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="school">École hôtelière *</Label>
              <Select
                value={form.school}
                onValueChange={(v) => setForm({ ...form, school: v })}
              >
                <SelectTrigger id="school">
                  <SelectValue placeholder="Sélectionne ton école" />
                </SelectTrigger>
                <SelectContent>
                  {SCHOOLS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="graduationYear">Année de diplôme *</Label>
              <Input
                id="graduationYear"
                required
                value={form.graduationYear}
                onChange={update("graduationYear")}
                placeholder="2024"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="restaurantName">Nom du restaurant *</Label>
              <Input
                id="restaurantName"
                required
                value={form.restaurantName}
                onChange={update("restaurantName")}
                placeholder="La Table de Marie"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="restaurantOpeningDate">Date d'ouverture *</Label>
              <Input
                id="restaurantOpeningDate"
                required
                value={form.restaurantOpeningDate}
                onChange={update("restaurantOpeningDate")}
                placeholder="Mars 2026"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message (optionnel)</Label>
            <Textarea
              id="message"
              rows={4}
              value={form.message}
              onChange={update("message")}
              placeholder="Parle-nous brièvement de ton projet, tes besoins, tes questions..."
            />
          </div>

          <div className="pt-2">
            <Button type="submit" size="lg" className="w-full" disabled={isPending}>
              {isPending ? "Envoi en cours..." : "Envoyer ma candidature"}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-3">
              Nous reviendrons vers toi sous 3 jours ouvrés pour vérification du diplôme et du registre du commerce.
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default HospitalityApplicationForm;
